import { yupResolver } from "@hookform/resolvers/yup";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { Controller, useForm } from "react-hook-form";
import {
  Close,
  ContentCopy,
  ExpandMore,
  HelpOutline,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { useEffect, useState } from "react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import PropTypes from "prop-types";
import Scrollbar from "../../../components/scrollbar/Scrollbar";
import { planSchema } from "../../../forms-schema/form.schema";
import {
  useCreatePlanMutation,
  useGetPlansQuery,
} from "../../../redux/services/plans.service";
import { useGetCountriesQuery } from "../../../redux/services/country.service";

// Helper component for field descriptions
const FieldDescription = ({ text }) => (
  <Typography
    variant="caption"
    color="text.secondary"
    sx={{ display: "block", mt: 0.5, mb: 1 }}
  >
    {text}
  </Typography>
);

FieldDescription.propTypes = {
  text: PropTypes.string.isRequired,
};

// Helper component for section headers
const SectionHeader = ({ title, description, icon }) => (
  <Box sx={{ mb: 2 }}>
    <Stack direction="row" alignItems="center" spacing={1}>
      {icon}
      <Typography variant="subtitle1" fontWeight="bold">
        {title}
      </Typography>
    </Stack>
    {description && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {description}
      </Typography>
    )}
  </Box>
);

SectionHeader.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  icon: PropTypes.node,
};

// Constants
const PLAN_TYPES = [
  {
    value: "RAZORPAY",
    label: "Razorpay (Recurring)",
    description: "Automatic recurring payments via Razorpay payment gateway",
  },
  {
    value: "RAZORPAY_ONE_TIME",
    label: "Razorpay (One-Time)",
    description: "Single payment via Razorpay, no auto-renewal",
  },
  {
    value: "CUSTOM",
    label: "Custom/Manual",
    description: "Manual subscription assigned by admin, no payment gateway",
  },
  {
    value: "DEVICE",
    label: "Device-Based",
    description: "Subscription linked to a physical device",
  },
];

const PERIODS = [
  { value: "daily", label: "Daily", description: "Billed every day" },
  { value: "weekly", label: "Weekly", description: "Billed every week" },
  { value: "monthly", label: "Monthly", description: "Billed every month" },
  {
    value: "quarterly",
    label: "Quarterly",
    description: "Billed every 3 months",
  },
  { value: "yearly", label: "Yearly", description: "Billed every year" },
];

const CURRENCIES = [
  { value: "INR", label: "INR - Indian Rupee (₹)", symbol: "₹" },
  { value: "USD", label: "USD - US Dollar ($)", symbol: "$" },
];

const APPLICATION_TYPES = [
  {
    value: "ONLINE",
    label: "Online Only",
    description: "Features work only with internet connection",
  },
  {
    value: "OFFLINE",
    label: "Offline Capable",
    description: "Features work without internet connection",
  },
];

// Feature configuration
const FEATURES = [
  {
    key: "ai-tutor",
    label: "AI Tutor",
    description: "AI-powered tutoring assistant",
  },
  {
    key: "smartEye",
    label: "Smart Eye",
    description: "Visual recognition and analysis",
  },
  {
    key: "mira",
    label: "MIRA",
    description: "Multi-modal intelligent reading assistant",
  },
  {
    key: "document_ai",
    label: "Document AI",
    description: "Document processing and analysis",
  },
  {
    key: "smart_image",
    label: "Smart Image",
    description: "Image recognition features",
  },
  {
    key: "objectMode",
    label: "Object Mode",
    description: "Object detection and identification",
  },
  {
    key: "textMode",
    label: "Text Mode",
    description: "Text recognition and processing",
  },
  {
    key: "currency",
    label: "Currency",
    description: "Currency recognition feature",
  },
];

CreatePlanForm.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

export default function CreatePlanForm({ open, onClose }) {
  const { enqueueSnackbar } = useSnackbar();
  const [expandedSection, setExpandedSection] = useState("basic");
  const [selectedPlanForLimits, setSelectedPlanForLimits] = useState(null);
  const [selectedPlanForFeatures, setSelectedPlanForFeatures] = useState(null);

  // JSON editor state
  const [jsonEditorValue, setJsonEditorValue] = useState("");
  const [jsonError, setJsonError] = useState("");

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(planSchema),
    defaultValues: {
      isRecurring: true,
      type: "RAZORPAY",
      period: "monthly",
      interval: 1,
      currency: "INR",
      applicationPlanType: "ONLINE",
      availableFeatures: {},
    },
  });

  // Reset form when drawer opens/closes
  useEffect(() => {
    if (open) {
      reset({
        isRecurring: true,
        type: "RAZORPAY",
        period: "monthly",
        interval: 1,
        currency: "INR",
        applicationPlanType: "ONLINE",
        availableFeatures: {},
      });
      // Reset copy selections
      setSelectedPlanForLimits(null);
      setSelectedPlanForFeatures(null);
    }
  }, [open, reset]);

  const [createPlan, { isLoading: isCreating }] = useCreatePlanMutation();

  // Watch values for conditional rendering
  const watchType = watch("type");
  const watchLinkedPlanId = watch("linkedPlanId");

  // Fetch countries
  const { data: countriesData, isLoading: isCountriesLoading } =
    useGetCountriesQuery(null, {});
  const countries = countriesData?.data || [];

  // Fetch existing plans for linked plan dropdown
  const { data: plansData, isLoading: isPlansLoading } = useGetPlansQuery(
    null,
    {},
  );
  const existingPlans = plansData?.data || [];

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedSection(isExpanded ? panel : false);
  };

  const handleFormSubmit = async (values) => {
    try {
      // Clean up the payload
      const payload = {
        ...values,
        // Convert amount to number if present
        amount: values.amount ? Number(values.amount) : undefined,
        interval: values.interval ? Number(values.interval) : undefined,
        durationDays: values.durationDays
          ? Number(values.durationDays)
          : undefined,
        // Format date if present
        offerValidTill: values.offerValidTill
          ? moment(values.offerValidTill).format("YYYY-MM-DD")
          : undefined,
      };

      // Remove empty/null values
      Object.keys(payload).forEach((key) => {
        if (
          payload[key] === undefined ||
          payload[key] === null ||
          payload[key] === ""
        ) {
          delete payload[key];
        }
      });

      const result = await createPlan(payload);

      if (result?.data?.success) {
        enqueueSnackbar("Plan created successfully!", {
          variant: "success",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        });
        reset();
        onClose();
      } else {
        enqueueSnackbar(result?.data?.message || "Failed to create plan", {
          variant: "error",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        });
      }
    } catch (error) {
      console.error("Error creating plan:", error);
      enqueueSnackbar("Error creating plan. Please try again.", {
        variant: "error",
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
      });
    }
  };

  // Copy usage limits from selected plan
  const handleCopyUsageLimits = (plan) => {
    if (!plan) return;

    // Set all usage limit fields from the selected plan
    // Use the actual value if it exists (including 0), otherwise leave undefined
    const setLimitValue = (fieldName, value) => {
      if (value !== undefined && value !== null) {
        setValue(fieldName, value);
      }
    };

    // Smart Eye limits
    setLimitValue("smarteyeTokenLimit", plan.smarteyeTokenLimit);
    setLimitValue("smarteyeConversationLimit", plan.smarteyeConversationLimit);
    setLimitValue("smarteyeTokenLimitShort", plan.smarteyeTokenLimitShort);

    // Smart Eye Currency limits
    setLimitValue(
      "smarteyeCurrencyTokenLimit",
      plan.smarteyeCurrencyTokenLimit,
    );
    setLimitValue(
      "smarteyeCurrencyConversationLimit",
      plan.smarteyeCurrencyConversationLimit,
    );

    // Smart Image limits
    setLimitValue("smartImageTokenLimit", plan.smartImageTokenLimit);
    setLimitValue(
      "smartImageConversationLimit",
      plan.smartImageConversationLimit,
    );
    setLimitValue("smartImageTokenLimitShort", plan.smartImageTokenLimitShort);

    // AI Chat limits
    setLimitValue("aiChatTokenLimit", plan.aiChatTokenLimit);
    setLimitValue("aiChatConversationLimit", plan.aiChatConversationLimit);

    // MIRA limits
    setLimitValue("miraTokenLimit", plan.miraTokenLimit);
    setLimitValue("miraConversationLimit", plan.miraConversationLimit);

    // Document AI limits
    setLimitValue("documentAskQnaLimit", plan.documentAskQnaLimit);
    setLimitValue("documentSummaryLimit", plan.documentSummaryLimit);
    setLimitValue("documentGenQnaLimit", plan.documentGenQnaLimit);

    // Other limits
    setLimitValue("textLimit", plan.textLimit);
    setLimitValue("documentPagesLimit", plan.documentPagesLimit);

    setSelectedPlanForLimits(plan);
    enqueueSnackbar(`Usage limits copied from "${plan.name}"`, {
      variant: "success",
      anchorOrigin: { vertical: "bottom", horizontal: "right" },
    });
  };

  // Copy feature access from selected plan
  const handleCopyFeatureAccess = (plan) => {
    if (!plan) return;

    // Set available features from the selected plan
    if (plan.availableFeatures) {
      // Deep clone to avoid reference issues
      const featuresCopy = JSON.parse(JSON.stringify(plan.availableFeatures));
      setValue("availableFeatures", featuresCopy, { shouldDirty: true });

      // Also populate the JSON editor
      setJsonEditorValue(JSON.stringify(featuresCopy, null, 2));
      setJsonError("");
    }

    setSelectedPlanForFeatures(plan);
    enqueueSnackbar(`Feature access configuration copied from "${plan.name}"`, {
      variant: "success",
      anchorOrigin: { vertical: "bottom", horizontal: "right" },
    });
  };

  // Apply JSON changes
  const handleApplyJson = () => {
    try {
      const parsed = JSON.parse(jsonEditorValue);
      setValue("availableFeatures", parsed, { shouldDirty: true });
      setJsonError("");
      enqueueSnackbar("Feature access configuration updated from JSON", {
        variant: "success",
      });
    } catch (error) {
      setJsonError(`Invalid JSON: ${error.message}`);
    }
  };

  // Format JSON
  const handleFormatJson = () => {
    try {
      const parsed = JSON.parse(jsonEditorValue);
      setJsonEditorValue(JSON.stringify(parsed, null, 2));
      setJsonError("");
    } catch (error) {
      setJsonError(`Invalid JSON: ${error.message}`);
    }
  };

  // Refresh JSON from form
  const handleRefreshJson = () => {
    const currentFeatures = watch("availableFeatures") || {};
    setJsonEditorValue(JSON.stringify(currentFeatures, null, 2));
    setJsonError("");
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 600, md: 700 },
          border: "none",
          overflow: "hidden",
        },
      }}
    >
      <Scrollbar>
        <Stack spacing={2} sx={{ p: 3 }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h5" fontWeight="bold">
              Create New Plan
            </Typography>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Fill in the details below to create a new subscription plan.
              Fields marked with <span style={{ color: "red" }}>*</span> are
              required. Hover over the{" "}
              <HelpOutline fontSize="small" sx={{ verticalAlign: "middle" }} />{" "}
              icons for more information.
            </Typography>
          </Alert>

          <Divider />

          <form onSubmit={handleSubmit(handleFormSubmit)}>
            {/* Section 1: Basic Information */}
            <Accordion
              expanded={expandedSection === "basic"}
              onChange={handleAccordionChange("basic")}
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle1" fontWeight="bold">
                  📝 Basic Information
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <Box>
                    <TextField
                      fullWidth
                      label={
                        <>
                          Plan Name <span style={{ color: "red" }}>*</span>
                        </>
                      }
                      {...register("name")}
                      error={Boolean(errors.name)}
                      helperText={errors.name?.message}
                      placeholder="e.g., Basic Plan, Premium Monthly"
                    />
                    <FieldDescription text="This is the name users will see when choosing a plan. Make it clear and descriptive." />
                  </Box>

                  <Box>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Description"
                      {...register("description")}
                      error={Boolean(errors.description)}
                      helperText={errors.description?.message}
                      placeholder="Describe what this plan offers..."
                    />
                    <FieldDescription text="A detailed description of the plan's benefits and features. This helps users understand what they're getting." />
                  </Box>

                  <Box>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="Razorpay Description"
                      {...register("razorpayDescription")}
                      error={Boolean(errors.razorpayDescription)}
                      helperText={errors.razorpayDescription?.message}
                      placeholder="Short description for payment page..."
                    />
                    <FieldDescription text="This text appears on the Razorpay checkout page. Keep it short and clear (max 255 characters)." />
                  </Box>
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Section 2: Plan Type & Billing */}
            <Accordion
              expanded={expandedSection === "billing"}
              onChange={handleAccordionChange("billing")}
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle1" fontWeight="bold">
                  💳 Plan Type & Billing
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={3}>
                  {/* Plan Type */}
                  <Box>
                    <FormControl fullWidth error={Boolean(errors.type)}>
                      <InputLabel>
                        Plan Type <span style={{ color: "red" }}>*</span>
                      </InputLabel>
                      <Controller
                        name="type"
                        control={control}
                        render={({ field }) => (
                          <Select {...field} label="Plan Type *">
                            {PLAN_TYPES.map((type) => (
                              <MenuItem key={type.value} value={type.value}>
                                <Box>
                                  <Typography>{type.label}</Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {type.description}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      <FormHelperText>{errors.type?.message}</FormHelperText>
                    </FormControl>
                    <FieldDescription text="Choose how this plan will be processed. Razorpay plans use automatic payment processing, Custom plans are assigned manually by admins." />
                  </Box>

                  {/* Recurring Toggle - Only for Razorpay */}
                  {watchType === "RAZORPAY" && (
                    <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Box>
                          <Typography variant="subtitle2">
                            Recurring Subscription
                            <Tooltip title="If enabled, users will be charged automatically at each billing cycle">
                              <HelpOutline
                                fontSize="small"
                                sx={{ ml: 1, verticalAlign: "middle" }}
                              />
                            </Tooltip>
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Automatically charge users at each billing cycle
                          </Typography>
                        </Box>
                        <Controller
                          name="isRecurring"
                          control={control}
                          render={({ field }) => (
                            <Switch
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                            />
                          )}
                        />
                      </Stack>
                    </Paper>
                  )}

                  {/* Period Selection - For Razorpay */}
                  {(watchType === "RAZORPAY" ||
                    watchType === "RAZORPAY_ONE_TIME") && (
                    <>
                      <Box>
                        <FormControl fullWidth error={Boolean(errors.period)}>
                          <InputLabel>
                            Billing Period{" "}
                            <span style={{ color: "red" }}>*</span>
                          </InputLabel>
                          <Controller
                            name="period"
                            control={control}
                            render={({ field }) => (
                              <Select {...field} label="Billing Period *">
                                {PERIODS.map((period) => (
                                  <MenuItem
                                    key={period.value}
                                    value={period.value}
                                  >
                                    {period.label} - {period.description}
                                  </MenuItem>
                                ))}
                              </Select>
                            )}
                          />
                          <FormHelperText>
                            {errors.period?.message}
                          </FormHelperText>
                        </FormControl>
                        <FieldDescription text="How often users will be billed. For example, 'Monthly' means users pay once every month." />
                      </Box>

                      <Box>
                        <TextField
                          fullWidth
                          label="Period Display Name"
                          {...register("periodName")}
                          placeholder="e.g., 1 Month, 3 Months, 1 Year"
                        />
                        <FieldDescription text="A friendly name shown to users, like '1 Month' or '6 Months'. This helps users understand the billing cycle." />
                      </Box>

                      <Box>
                        <TextField
                          fullWidth
                          type="number"
                          label={
                            <>
                              Interval <span style={{ color: "red" }}>*</span>
                            </>
                          }
                          {...register("interval", { valueAsNumber: true })}
                          error={Boolean(errors.interval)}
                          helperText={errors.interval?.message}
                          placeholder="1"
                          InputProps={{
                            inputProps: { min: 1 },
                          }}
                        />
                        <FieldDescription text="How many periods between each billing. For example, interval=3 with monthly period means billing every 3 months." />
                      </Box>
                    </>
                  )}

                  {/* Duration Days - For Custom/Device */}
                  {(watchType === "CUSTOM" || watchType === "DEVICE") && (
                    <Box>
                      <TextField
                        fullWidth
                        type="number"
                        label={
                          <>
                            Duration (Days){" "}
                            <span style={{ color: "red" }}>*</span>
                          </>
                        }
                        {...register("durationDays", { valueAsNumber: true })}
                        error={Boolean(errors.durationDays)}
                        helperText={errors.durationDays?.message}
                        placeholder="e.g., 30, 90, 365"
                        InputProps={{
                          inputProps: { min: 1 },
                        }}
                      />
                      <FieldDescription text="How many days the subscription will last. For example, 30 = 1 month, 365 = 1 year." />
                    </Box>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Section 3: Pricing */}
            <Accordion
              expanded={expandedSection === "pricing"}
              onChange={handleAccordionChange("pricing")}
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle1" fontWeight="bold">
                  💰 Pricing
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <Box>
                    <TextField
                      fullWidth
                      type="number"
                      label={
                        <>
                          Amount <span style={{ color: "red" }}>*</span>
                        </>
                      }
                      {...register("amount", { valueAsNumber: true })}
                      error={Boolean(errors.amount)}
                      helperText={errors.amount?.message}
                      placeholder="e.g., 5999 for ₹59.99"
                      InputProps={{
                        inputProps: { min: 0 },
                        startAdornment: (
                          <InputAdornment position="start">
                            {watch("currency") === "INR" ? "₹" : "$"}
                          </InputAdornment>
                        ),
                      }}
                    />
                    <FieldDescription text="Enter the price in the smallest currency unit. For INR: 5999 = ₹59.99, 100 = ₹1.00. For USD: 999 = $9.99" />
                  </Box>

                  <Box>
                    <FormControl fullWidth error={Boolean(errors.currency)}>
                      <InputLabel>
                        Currency <span style={{ color: "red" }}>*</span>
                      </InputLabel>
                      <Controller
                        name="currency"
                        control={control}
                        render={({ field }) => (
                          <Select {...field} label="Currency *">
                            {CURRENCIES.map((curr) => (
                              <MenuItem key={curr.value} value={curr.value}>
                                {curr.label}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      <FormHelperText>
                        {errors.currency?.message}
                      </FormHelperText>
                    </FormControl>
                    <FieldDescription text="The currency in which users will be charged." />
                  </Box>

                  <Box>
                    <FormControl fullWidth error={Boolean(errors.countryCode)}>
                      <InputLabel>
                        Country <span style={{ color: "red" }}>*</span>
                      </InputLabel>
                      <Controller
                        name="countryCode"
                        control={control}
                        render={({ field }) => (
                          <Select {...field} label="Country *">
                            {isCountriesLoading ? (
                              <MenuItem disabled>
                                <CircularProgress size={24} />
                              </MenuItem>
                            ) : (
                              countries.map((country) => (
                                <MenuItem
                                  key={country.id}
                                  value={country.countryMobileCode}
                                >
                                  {country.countryName} (
                                  {country.countryMobileCode})
                                </MenuItem>
                              ))
                            )}
                          </Select>
                        )}
                      />
                      <FormHelperText>
                        {errors.countryCode?.message}
                      </FormHelperText>
                    </FormControl>
                    <FieldDescription text="The country where this plan will be available. This affects regional pricing and availability." />
                  </Box>
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Section 4: Offer Configuration */}
            <Accordion
              expanded={expandedSection === "offer"}
              onChange={handleAccordionChange("offer")}
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle1" fontWeight="bold">
                  🎁 Offer Configuration (Optional)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Link this plan to another plan to create a discounted offer.
                    The linked plan's price will be used as the "original price"
                    for comparison.
                  </Alert>

                  <Box>
                    <FormControl fullWidth>
                      <InputLabel>Link to Existing Plan</InputLabel>
                      <Controller
                        name="linkedPlanId"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            label="Link to Existing Plan"
                            value={field.value || ""}
                          >
                            <MenuItem value="">
                              <em>None (No linked plan)</em>
                            </MenuItem>
                            {isPlansLoading ? (
                              <MenuItem disabled>
                                <CircularProgress size={24} />
                              </MenuItem>
                            ) : (
                              existingPlans.map((plan) => (
                                <MenuItem key={plan.id} value={plan.id}>
                                  {plan.name} - {plan.amount / 100}{" "}
                                  {plan.currency}
                                </MenuItem>
                              ))
                            )}
                          </Select>
                        )}
                      />
                    </FormControl>
                    <FieldDescription text="Select an existing plan to show as the 'original price'. This creates a discount comparison on the checkout page." />
                  </Box>

                  {watchLinkedPlanId && (
                    <Box>
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <Controller
                          name="offerValidTill"
                          control={control}
                          render={({ field }) => (
                            <DatePicker
                              label={
                                <>
                                  Offer Valid Till{" "}
                                  <span style={{ color: "red" }}>*</span>
                                </>
                              }
                              value={field.value ? moment(field.value) : null}
                              onChange={(date) => field.onChange(date)}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  error: Boolean(errors.offerValidTill),
                                  helperText: errors.offerValidTill?.message,
                                },
                              }}
                              minDate={moment()}
                            />
                          )}
                        />
                      </LocalizationProvider>
                      <FieldDescription text="The date until which this offer price is valid. After this date, the offer will expire." />
                    </Box>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Section 5: Usage Limits */}
            <Accordion
              expanded={expandedSection === "limits"}
              onChange={handleAccordionChange("limits")}
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle1" fontWeight="bold">
                  📊 Usage Limits
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {/* Copy from existing plan */}
                <Paper
                  sx={{
                    p: 2,
                    mb: 3,
                    bgcolor: "primary.50",
                    border: "1px dashed",
                    borderColor: "primary.main",
                  }}
                >
                  <Stack spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <ContentCopy color="primary" fontSize="small" />
                      <Typography variant="subtitle2" color="primary.main">
                        Copy Usage Limits from Existing Plan
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      Select an existing plan to automatically fill all usage
                      limit values. You can modify them after copying.
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Autocomplete
                        fullWidth
                        size="small"
                        options={existingPlans}
                        getOptionLabel={(option) =>
                          `${option.name} (${option.type || "N/A"})`
                        }
                        value={selectedPlanForLimits}
                        onChange={(event, newValue) => {
                          if (newValue) {
                            handleCopyUsageLimits(newValue);
                          } else {
                            setSelectedPlanForLimits(null);
                          }
                        }}
                        loading={isPlansLoading}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Plan to Copy From"
                            placeholder="Search plans..."
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {isPlansLoading ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id}>
                            <Box>
                              <Typography variant="body2">
                                {option.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Type: {option.type || "N/A"} | Amount:{" "}
                                {option.amount
                                  ? `${option.amount / 100} ${option.currency}`
                                  : "Free"}
                              </Typography>
                            </Box>
                          </li>
                        )}
                      />
                    </Stack>
                    {selectedPlanForLimits && (
                      <Alert severity="success" sx={{ mt: 1 }}>
                        Limits copied from:{" "}
                        <strong>{selectedPlanForLimits.name}</strong>
                      </Alert>
                    )}
                  </Stack>
                </Paper>

                <Alert severity="info" sx={{ mb: 2 }}>
                  Set limits for each feature. Leave empty for unlimited usage.
                  Token limits control how much AI processing a user can use.
                  Conversation limits control how many separate conversations
                  they can have.
                </Alert>

                <Grid container spacing={2}>
                  {/* Smart Eye Limits */}
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, color: "primary.main" }}
                    >
                      👁️ Smart Eye
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Token Limit"
                      {...register("smarteyeTokenLimit", {
                        valueAsNumber: true,
                      })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Maximum AI tokens per billing cycle" />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Conversation Limit"
                      {...register("smarteyeConversationLimit", {
                        valueAsNumber: true,
                      })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Maximum conversations allowed" />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Short Token Limit"
                      {...register("smarteyeTokenLimitShort", {
                        valueAsNumber: true,
                      })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Tokens for quick responses" />
                  </Grid>

                  {/* Smart Eye Currency Limits */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, color: "primary.main" }}
                    >
                      💰 Smart Eye Currency
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Token Limit"
                      {...register("smarteyeCurrencyTokenLimit", {
                        valueAsNumber: true,
                      })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Maximum tokens for currency recognition" />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Conversation Limit"
                      {...register("smarteyeCurrencyConversationLimit", {
                        valueAsNumber: true,
                      })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Maximum currency recognition conversations" />
                  </Grid>

                  {/* Smart Image Limits */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, color: "primary.main" }}
                    >
                      🖼️ Smart Image
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Token Limit"
                      {...register("smartImageTokenLimit", {
                        valueAsNumber: true,
                      })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Maximum AI tokens for image processing" />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Conversation Limit"
                      {...register("smartImageConversationLimit", {
                        valueAsNumber: true,
                      })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Maximum image conversations" />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Short Token Limit"
                      {...register("smartImageTokenLimitShort", {
                        valueAsNumber: true,
                      })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Tokens for quick image responses" />
                  </Grid>

                  {/* AI Chat Limits */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, color: "primary.main" }}
                    >
                      🤖 AI Chat
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Token Limit"
                      {...register("aiChatTokenLimit", { valueAsNumber: true })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Maximum AI tokens for chat" />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Conversation Limit"
                      {...register("aiChatConversationLimit", {
                        valueAsNumber: true,
                      })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Maximum chat conversations" />
                  </Grid>

                  {/* MIRA Limits */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, color: "primary.main" }}
                    >
                      📚 MIRA
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Token Limit"
                      {...register("miraTokenLimit", { valueAsNumber: true })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Maximum tokens for MIRA" />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Conversation Limit"
                      {...register("miraConversationLimit", {
                        valueAsNumber: true,
                      })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Maximum MIRA conversations" />
                  </Grid>

                  {/* Document AI Limits */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, color: "primary.main" }}
                    >
                      📑 Document AI
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Ask Q&A Limit"
                      {...register("documentAskQnaLimit", {
                        valueAsNumber: true,
                      })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Maximum document Q&A queries" />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Summary Limit"
                      {...register("documentSummaryLimit", {
                        valueAsNumber: true,
                      })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Maximum document summaries" />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Generate Q&A Limit"
                      {...register("documentGenQnaLimit", {
                        valueAsNumber: true,
                      })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Maximum generated Q&A sets" />
                  </Grid>

                  {/* Other Limits */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, color: "primary.main" }}
                    >
                      📄 Other Limits
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Text Limit"
                      {...register("textLimit", { valueAsNumber: true })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Text recognitions" />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Document Pages Limit"
                      {...register("documentPagesLimit", {
                        valueAsNumber: true,
                      })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Maximum document pages" />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Section 6: Feature Access */}
            <Accordion
              expanded={expandedSection === "features"}
              onChange={handleAccordionChange("features")}
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle1" fontWeight="bold">
                  ⚙️ Feature Access Configuration
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {/* Copy from existing plan */}
                <Paper
                  sx={{
                    p: 2,
                    mb: 3,
                    bgcolor: "secondary.50",
                    border: "1px dashed",
                    borderColor: "secondary.main",
                  }}
                >
                  <Stack spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <ContentCopy color="secondary" fontSize="small" />
                      <Typography variant="subtitle2" color="secondary.main">
                        Copy Feature Access from Existing Plan
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      Select an existing plan to automatically copy all feature
                      access settings (online/offline toggles). You can modify
                      them after copying.
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Autocomplete
                        fullWidth
                        size="small"
                        options={existingPlans}
                        getOptionLabel={(option) =>
                          `${option.name} (${option.type || "N/A"})`
                        }
                        value={selectedPlanForFeatures}
                        onChange={(event, newValue) => {
                          if (newValue) {
                            handleCopyFeatureAccess(newValue);
                          } else {
                            setSelectedPlanForFeatures(null);
                          }
                        }}
                        loading={isPlansLoading}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Plan to Copy From"
                            placeholder="Search plans..."
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {isPlansLoading ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id}>
                            <Box>
                              <Typography variant="body2">
                                {option.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Type: {option.type || "N/A"} | Features:{" "}
                                {option.availableFeatures
                                  ? Object.keys(option.availableFeatures).length
                                  : 0}{" "}
                                configured
                              </Typography>
                            </Box>
                          </li>
                        )}
                      />
                    </Stack>
                    {selectedPlanForFeatures && (
                      <Alert severity="success" sx={{ mt: 1 }}>
                        Feature access copied from:{" "}
                        <strong>{selectedPlanForFeatures.name}</strong>
                      </Alert>
                    )}
                  </Stack>
                </Paper>

                <Alert severity="info" sx={{ mb: 2 }}>
                  Edit the JSON directly to configure feature access. Use the
                  "Copy from Existing Plan" option above to start with an
                  existing configuration.
                </Alert>

                <TextField
                  fullWidth
                  multiline
                  rows={20}
                  value={jsonEditorValue}
                  onChange={(e) => {
                    setJsonEditorValue(e.target.value);
                    setJsonError("");
                  }}
                  error={Boolean(jsonError)}
                  helperText={jsonError}
                  placeholder='{"feature": {"capability": {"isOnline": true, "isOffline": false}}}'
                  sx={{
                    "& .MuiInputBase-input": {
                      fontFamily: "monospace",
                      fontSize: "0.85rem",
                    },
                  }}
                />
                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleApplyJson}
                    disabled={!jsonEditorValue.trim()}
                  >
                    Apply JSON
                  </Button>
                  <Button variant="outlined" onClick={handleFormatJson}>
                    Format JSON
                  </Button>
                  <Button variant="outlined" onClick={handleRefreshJson}>
                    Refresh from Form
                  </Button>
                </Stack>

                <Divider sx={{ my: 3 }} />

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  JSON Structure Reference:
                </Typography>
                <Paper sx={{ p: 2, bgcolor: "grey.100" }}>
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.75rem",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {`{
  "featureName": {
    "create": { "isOnline": true, "isOffline": false },
    "askQuestion": { "isOnline": true, "isOffline": true },
    "history": { "isOnline": true, "isOffline": false },
    "reader": { "isOnline": false, "isOffline": true },
    "genQuestion": { "isOnline": true, "isOffline": false }
  },
  "anotherFeature": {
    // ... same structure
  }
}

Available default features: ${FEATURES.map((f) => f.key).join(", ")}
Available capabilities: create, askQuestion, history, reader, genQuestion`}
                  </Typography>
                </Paper>
              </AccordionDetails>
            </Accordion>

            {/* Section 7: Application Type */}
            <Accordion
              expanded={expandedSection === "appType"}
              onChange={handleAccordionChange("appType")}
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle1" fontWeight="bold">
                  📱 Application Type
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Choose whether this plan is for online-only use or includes
                  offline capabilities.
                </Alert>

                <Controller
                  name="applicationPlanType"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup {...field}>
                      {APPLICATION_TYPES.map((type) => (
                        <Paper key={type.value} sx={{ p: 2, mb: 1 }}>
                          <FormControlLabel
                            value={type.value}
                            control={<Radio />}
                            label={
                              <Box>
                                <Typography variant="subtitle2">
                                  {type.label}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {type.description}
                                </Typography>
                              </Box>
                            }
                          />
                        </Paper>
                      ))}
                    </RadioGroup>
                  )}
                />
              </AccordionDetails>
            </Accordion>

            {/* Submit Button */}
            <Box sx={{ mt: 3 }}>
              <LoadingButton
                fullWidth
                type="submit"
                variant="contained"
                size="large"
                loading={isCreating}
              >
                Create Plan
              </LoadingButton>
            </Box>
          </form>
        </Stack>
      </Scrollbar>
    </Drawer>
  );
}
